import { Injectable } from '@angular/core'
import { KeycloakService, type KeycloakOptions, type KeycloakEvent, KeycloakEventType } from '@emilfreydigital/keycloak-angular'
import { HubConfigurationService } from '@inside-hub-app/hub-configuration'
import { NGXLogger } from 'ngx-logger'
import { type Subscription, BehaviorSubject, forkJoin, mergeMap, type Observable, from, map, of } from 'rxjs'
import { ImpersonateUserService } from './impersonate-user.service'
import { Router } from '@angular/router'
import { AuthService } from './auth.service'

@Injectable({
  providedIn: 'root'
})

export class KcInitService {
  kcReady = new BehaviorSubject<boolean>(null)
  kcReady$ = this.kcReady.asObservable()

  constructor (
    private readonly service: KeycloakService,
    private readonly hubConfigurationService: HubConfigurationService,
    private readonly logger: NGXLogger,
    private readonly impersonateService: ImpersonateUserService,
    private readonly router: Router,
    private readonly authService: AuthService
  ) {}

  kcInit (redirectUri?: string): void {
    const subscription: Subscription = this.service.keycloakEvents$
      .subscribe((events: KeycloakEvent) => {
        if (events.type === KeycloakEventType.OnReady) {
          subscription.unsubscribe()
          this.kcReady.next(true)
        }
      })

    if (window['cordova']) {
      document.addEventListener('deviceready', () => {
        this.initialize().subscribe()
      }, false)
    } else {
      this.initialize(redirectUri).subscribe()
    }
  }

  initialize (redirectUri?: string): Observable<void> {
    this.logger.debug('initializing keycloak')
    return forkJoin({
      isImpersonated: this.impersonateService.isUserImpersonated(),
      token: this.authService.getToken()
    }).pipe(
      mergeMap(results => {
        if (results.isImpersonated) {
          // do not initialize keycloak at all
          return of({ ...results, initialized: true })
        } else {
          return from(this.initializeWithNormalClient(results.token, redirectUri).then(initialized => {
            return { ...results, initialized }
          }))
        }
      }),
      mergeMap(results => {
        if (results.isImpersonated && !results.initialized) {
          return forkJoin({
            loginInfo: this.authService.removeLoginInfo(),
            token: this.authService.removeToken()
          }).pipe(mergeMap(() => {
            return from(this.initializeWithNormalClient(results.token, redirectUri))
          }))
        }
        return of(results)
      }),
      map(() => undefined)
    )
  }

  private async initializeWithNormalClient (token: string, redirectUri?: string): Promise<boolean> {
    this.logger.debug('initializing keycloak with normal client', token, redirectUri)

    const clientId = this.hubConfigurationService.get<string>('hub.auth.keycloak.client-id')
    const localhostClientId = this.hubConfigurationService.get<string>('hub.auth.keycloak.client-id-localhost')

    // setup configuration
    const kcConfiguration: KeycloakOptions = {
      config: {
        url: this.hubConfigurationService.get<string>('hub.auth.keycloak.url'),
        realm: this.hubConfigurationService.get<string>('hub.auth.keycloak.realm'),
        clientId: window.location.host.includes('localhost') ? localhostClientId : clientId
      },
      initOptions: {
        onLoad: 'login-required',
        checkLoginIframe: false,
        enableLogging: true
      },
      enableBearerInterceptor: false,
      bearerPrefix: ''
    }

    if (window['cordova']) {
      kcConfiguration.initOptions['adapter'] = 'cordova'
      kcConfiguration.initOptions['responseMode'] = 'query'
      kcConfiguration.initOptions['redirectUri'] = 'ionic://localhost/login'
    }

    if (redirectUri) {
      kcConfiguration.initOptions['redirectUri'] = redirectUri
    }

    // initialize
    return await this.service.init(kcConfiguration).then(() => {
      this.logger.debug('keycloak initialized (normal) - user authenticated', kcConfiguration)
      return true
    }).catch((e) => {
      this.logger.error('error while authenticating user (normal)', e, kcConfiguration)
      return false
    })
  }
}
